/**
 * Left-nav ordering (impl-INV-A / DEC-17): extensions order by their
 * DB-persisted `position`; Core entries are pinned (episodes leads,
 * the extensions catalog trails). Ties and missing positions break by id so
 * the rail is deterministic.
 */

const PINNED_LEADING = ["episodes"] as const;
const PINNED_TRAILING = ["extensions"] as const;

type Rank = readonly [band: number, slot: number, id: string];

function rankOf(id: string, positions: ReadonlyMap<string, number>): Rank {
  const lead = PINNED_LEADING.indexOf(id as (typeof PINNED_LEADING)[number]);
  if (lead >= 0) return [0, lead, id];
  const trail = PINNED_TRAILING.indexOf(id as (typeof PINNED_TRAILING)[number]);
  if (trail >= 0) return [2, trail, id];
  return [1, positions.get(id) ?? Number.MAX_SAFE_INTEGER, id];
}

/**
 * Whether a module should appear in the left rail. A CATALOG module (present in
 * `catalog`) shows only while installed AND enabled — disabling/uninstalling it
 * removes the tab. A module not in the catalog is Core (episodes/settings/
 * extensions) and is always shown.
 */
export function isRailModuleVisible(
  id: string,
  catalog: ReadonlyMap<string, { readonly installed: boolean; readonly enabled: boolean }>,
): boolean {
  const ext = catalog.get(id);
  return ext === undefined || (ext.installed && ext.enabled);
}

export function orderRailModules<T extends { readonly id: string }>(
  modules: readonly T[],
  positions: ReadonlyMap<string, number>,
): T[] {
  return [...modules].sort((a, b) => {
    const ra = rankOf(a.id, positions);
    const rb = rankOf(b.id, positions);
    return (
      ra[0] - rb[0] || ra[1] - rb[1] || ra[2].localeCompare(rb[2])
    );
  });
}
